import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, Camera, X as XIcon, ChevronDown, ChevronUp,
  Package as PackageIcon, Layers, Search, Eye, EyeOff, Star, Pencil,
  CheckCircle2, AlertTriangle, ChevronRight, RotateCcw,
  ShoppingBag, Calendar, Minus, RefreshCw, Tag,
} from "lucide-react";
import { Card, Btn, Input, Field } from "@/components/admin/ui";
import { StrollerAttributesEditor } from "@/pages/admin/StrollerAttributesEditor";
import { EcvAttributesEditor } from "@/pages/admin/EcvAttributesEditor";
import { API, BACKEND_URL } from "@/config";
import { fmtErr } from "@/context/AuthProvider";
import { slugify, STROLLER_SLUG_MAP } from "@/utils/seo";

/* ─────────────────────────────────────────────────────────── helpers ── */
const emptyProduct = {
  name: "", tagline: "", description: "", category: "", subcategory: "",
  tier_1_3: 20, tier_4_7: 16, tier_8_plus: 13, image: "",
  features: [], min_age: "", buffer_days: 1, target_units: 1,
  related: [], active: true, seo_title: "", seo_description: "", seo_slug: "",
  website_visible: true, available_units_override: "", featured: false,
};

const STATUS_STYLE = {
  available: "text-emerald-300 border-emerald-500/40 bg-emerald-500/8",
  rented:    "text-[#FFD166] border-[#FFD166]/40 bg-[#FFD166]/8",
  retired:   "text-[#B9C8D5] border-[#B9C8D5]/25 bg-black/20",
};
const COND_STYLE = {
  good: "text-emerald-300",
  damaged: "text-red-300",
  out_of_service: "text-amber-300",
};

/* ─────────────────────────────────────────── LEFT PANEL — product list ── */
const ProductListItem = ({ p, selected, onClick }) => {
  const avail = p.units_available ?? 0;
  const total = p.units_total ?? 0;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`product-list-item-${p.id}`}
      className={`w-full text-left flex items-center gap-3 p-2.5 rounded-xl border transition-all ${
        selected
          ? "border-[#FFD166]/60 bg-[#FFD166]/8"
          : "border-transparent hover:border-[#FFD166]/25 hover:bg-white/3"
      }`}
    >
      {/* Thumbnail */}
      <div className="w-12 h-12 rounded-lg border border-[#FFD166]/15 bg-black/40 shrink-0 overflow-hidden">
        {p.image
          ? <img src={p.image.startsWith("/api/") ? `${BACKEND_URL}${p.image}` : p.image} alt={p.name} className="w-full h-full object-cover" />
          : <PackageIcon className="w-5 h-5 text-[#B9C8D5] m-3.5" />
        }
      </div>
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#F6F1E4] leading-tight truncate">{p.name}</div>
        <div className="text-[10px] text-[#B9C8D5] mt-0.5 truncate">{p.category}{p.subcategory ? ` · ${p.subcategory}` : ""}</div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-[#FFD166]">${Number(p.tier_1_3).toFixed(2)}/d</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${avail > 0 ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/8" : "text-red-300 border-red-500/30 bg-red-500/8"}`}>
            {avail}/{total} avail
          </span>
          {!p.website_visible && <EyeOff className="w-3 h-3 text-[#B9C8D5]" />}
          {p.featured && <Star className="w-3 h-3 text-[#FFD166]" />}
        </div>
      </div>
    </button>
  );
};

/* ─────────────────────────────────────────── SECTION CARD wrapper ── */
const Section = ({ title, icon: Icon, children, defaultOpen = true, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-[#FFD166]/15 bg-black/20 overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#FFD166]" />}
          <span className="text-sm font-medium text-[#FFD166]">{title}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-[#B9C8D5] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-1 space-y-3">{children}</div>}
    </div>
  );
};

/* ─────────────────────────────────────────── INLINE INVENTORY ── */
const InlineInventory = ({ productId, onHandLive, onRefresh }) => {
  const [units, setUnits] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products/${productId}/inventory-units`);
      setUnits(data.units || []);
      setSummary(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [productId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (onHandLive !== null) load(); }, [onHandLive]); // eslint-disable-line

  const updateUnit = async (unitId, changes) => {
    setUpdatingId(unitId);
    try {
      await axios.patch(`${API}/admin/products/${productId}/inventory-units/${unitId}`, changes);
      await load();
      onRefresh?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setUpdatingId(null); }
  };

  if (loading) return <div className="text-xs text-[#B9C8D5] py-2">Loading units…</div>;

  const active = units.filter(u => u.status !== "retired");
  const retired = units.filter(u => u.status === "retired");

  return (
    <div className="space-y-2">
      {/* Summary row */}
      <div className="flex gap-2 flex-wrap text-xs">
        <span className="px-2 py-1 rounded border border-emerald-500/30 text-emerald-300 bg-emerald-500/8">
          {summary.available_count ?? 0} available
        </span>
        <span className="px-2 py-1 rounded border border-[#FFD166]/30 text-[#FFD166] bg-[#FFD166]/8">
          {summary.rented_count ?? 0} rented
        </span>
        {(summary.damaged_count ?? 0) > 0 && (
          <span className="px-2 py-1 rounded border border-red-500/30 text-red-300 bg-red-500/8">
            {summary.damaged_count} damaged/oos
          </span>
        )}
        <button onClick={load} className="ml-auto text-[#B9C8D5] hover:text-[#FFD166]" title="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>

      {/* Active units */}
      {active.length === 0 ? (
        <p className="text-xs text-[#B9C8D5] italic">No active units — set stock count above to add.</p>
      ) : (
        <div className="space-y-1.5">
          {active.map(u => (
            <div
              key={u.id}
              className="flex items-center gap-2 rounded-lg border border-[#FFD166]/10 bg-black/30 px-3 py-2"
              data-testid={`unit-row-${u.id}`}
            >
              <Tag className="w-3 h-3 text-[#B9C8D5] shrink-0" />
              <span className="text-[11px] font-mono text-[#B9C8D5] truncate flex-1">{u.barcode || u.id?.slice(0, 8).toUpperCase()}</span>
              {/* Status selector */}
              <select
                value={u.status}
                onChange={(e) => updateUnit(u.id, { status: e.target.value })}
                disabled={updatingId === u.id}
                className={`text-[11px] rounded border px-1.5 py-0.5 bg-black/40 cursor-pointer ${STATUS_STYLE[u.status] || ""}`}
                data-testid={`unit-status-${u.id}`}
              >
                <option value="available">available</option>
                <option value="rented">rented</option>
              </select>
              {/* Condition selector */}
              <select
                value={u.condition || "good"}
                onChange={(e) => updateUnit(u.id, { condition: e.target.value })}
                disabled={updatingId === u.id}
                className={`text-[11px] rounded border border-[#B9C8D5]/20 px-1.5 py-0.5 bg-black/40 cursor-pointer ${COND_STYLE[u.condition] || "text-emerald-300"}`}
                data-testid={`unit-condition-${u.id}`}
              >
                <option value="good">good</option>
                <option value="damaged">damaged</option>
                <option value="out_of_service">oos</option>
              </select>
            </div>
          ))}
        </div>
      )}

      {retired.length > 0 && (
        <details className="text-[11px] text-[#B9C8D5]">
          <summary className="cursor-pointer hover:text-[#F6F1E4]">{retired.length} retired unit{retired.length !== 1 ? "s" : ""}</summary>
          <div className="mt-1 space-y-1">
            {retired.map(u => (
              <div key={u.id} className="flex items-center gap-2 opacity-50 px-3 py-1 rounded border border-[#B9C8D5]/10 bg-black/20">
                <Tag className="w-3 h-3" />
                <span className="font-mono truncate flex-1">{u.barcode || u.id?.slice(0, 8).toUpperCase()}</span>
                <span className="text-[10px]">retired</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────── BOOKINGS SECTION ── */
const BookingsSection = ({ productId }) => {
  const [tab, setTab] = useState("future");
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    axios.get(`${API}/admin/products/${productId}/reservations`)
      .then(r => setAll(r.data.reservations || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [productId]);

  const filtered = useMemo(() => {
    return all.filter(r => r._bucket === tab).slice(0, 30);
  }, [all, tab]);

  const counts = useMemo(() => {
    const c = { future: 0, current: 0, past: 0, cancelled: 0 };
    all.forEach(r => { if (c[r._bucket] !== undefined) c[r._bucket]++; });
    return c;
  }, [all]);

  const statusColor = s => s === "confirmed" ? "text-emerald-300" : s === "cancelled" ? "text-red-300" : "text-[#FFD166]";

  if (loading) return <div className="text-xs text-[#B9C8D5] py-2">Loading bookings…</div>;

  return (
    <div className="space-y-3">
      {/* Tab strip */}
      <div className="flex gap-1 flex-wrap">
        {[["future","Upcoming"], ["current","Current"], ["past","Past"], ["cancelled","Cancelled"]].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`text-[11px] px-3 py-1.5 rounded-full border ${tab === k ? "bg-[#FFD166]/15 border-[#FFD166]/60 text-[#FFD166]" : "border-[#B9C8D5]/25 text-[#B9C8D5] hover:border-[#FFD166]/30"}`}
            data-testid={`bookings-tab-${k}`}
          >
            {label} ({counts[k]})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-xs text-[#B9C8D5] italic py-2">No {tab} bookings for this product.</p>
      ) : (
        <div className="space-y-1.5">
          {filtered.map(r => (
            <div key={r.id} className="rounded-lg border border-[#FFD166]/10 bg-black/20 px-3 py-2 text-xs" data-testid={`booking-row-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-[#F6F1E4] font-medium">{r.customer_name || "—"}</span>
                <span className={`uppercase tracking-wide text-[10px] ${statusColor(r.status)}`}>{r.status}</span>
              </div>
              <div className="text-[#B9C8D5] mt-0.5">
                {r.delivery_date} → {r.pickup_date} · {r.delivery_resort || r.delivery_address || ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────── RIGHT PANEL — edit form ── */
const ProductEditor = ({ draft, cats, allProducts, onClose, onSaved }) => {
  const initialSlug = draft.seo_slug || STROLLER_SLUG_MAP[draft.id] || "";
  const [f, setF] = useState({
    ...draft,
    features: draft.features?.join?.("\n") || (Array.isArray(draft.features) ? draft.features.join("\n") : draft.features || ""),
    related: draft.related || [],
    free_accessories: draft.free_accessories || [],
    option_groups: draft.option_groups || [],
    addon_product_ids: draft.addon_product_ids || [],
    on_hand: draft.on_hand ?? "",
    seo_title: draft.seo_title || "",
    seo_description: draft.seo_description || "",
    seo_slug: initialSlug,
  });
  const [saving, setSaving] = useState(false);
  const [onHandLive, setOnHandLive] = useState(null);
  const [adjustingStock, setAdjustingStock] = useState(false);
  const isNew = !draft.id;
  const subcats = (cats.find(c => c.id === f.category)?.subcategories) || [];

  const prevNameRef = useRef(draft.name || "");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(!!initialSlug);
  useEffect(() => {
    if (!isNew || slugManuallyEdited) return;
    if (f.name === prevNameRef.current) return;
    prevNameRef.current = f.name;
    setF(cur => ({ ...cur, seo_slug: slugify(f.name) }));
  }, [f.name, isNew, slugManuallyEdited]);

  // Load live inventory count
  const loadOnHand = useCallback(async () => {
    if (isNew || !f.id) return;
    try {
      const r = await axios.get(`${API}/admin/products/${f.id}/on-hand`);
      setOnHandLive(r.data.on_hand);
      setF(cur => (cur.on_hand === "" || cur.on_hand === undefined ? { ...cur, on_hand: r.data.on_hand } : cur));
    } catch { /* ignore */ }
  }, [f.id, isNew]);
  useEffect(() => { loadOnHand(); }, [loadOnHand]);

  // Adjust stock directly
  const adjustStock = async (delta) => {
    const current = onHandLive ?? 0;
    const next = Math.max(0, Math.min(500, current + delta));
    if (next === current) return;
    setAdjustingStock(true);
    try {
      await axios.patch(`${API}/admin/products/${f.id}/on-hand`, { on_hand: next });
      setOnHandLive(next);
      setF(cur => ({ ...cur, on_hand: next }));
      toast.success(`Stock updated: ${current} → ${next}`);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setAdjustingStock(false); }
  };

  const setStockDirectly = async (val) => {
    const next = Math.max(0, Math.min(500, Number(val)));
    setF(cur => ({ ...cur, on_hand: next }));
  };

  const applyStockInput = async () => {
    const next = Number(f.on_hand);
    if (isNaN(next) || next === onHandLive) return;
    setAdjustingStock(true);
    try {
      await axios.patch(`${API}/admin/products/${f.id}/on-hand`, { on_hand: next });
      setOnHandLive(next);
      toast.success(`Stock set to ${next}`);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setAdjustingStock(false); }
  };

  const save = async () => {
    if (!isNew) {
      const currentEffective = slugify(draft.seo_slug || STROLLER_SLUG_MAP[draft.id] || "");
      const newSlug = slugify(f.seo_slug || "");
      if (newSlug && currentEffective && newSlug !== currentEffective && f.website_visible !== false && f.active !== false) {
        if (!window.confirm("Changing this product URL creates a permanent redirect. Existing links continue to work. Continue?")) return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...f,
        features: String(f.features).split("\n").map(s => s.trim()).filter(Boolean),
        tier_1_3: Number(f.tier_1_3), tier_4_7: Number(f.tier_4_7), tier_8_plus: Number(f.tier_8_plus),
        tier_auto_calc: !!f.tier_auto_calc,
        tier_4_7_discount_pct: Number(f.tier_4_7_discount_pct ?? 15),
        tier_8_plus_discount_pct: Number(f.tier_8_plus_discount_pct ?? f.tier_8_plus_premium_pct ?? 25),
        target_units: Number(f.target_units) || 1, buffer_days: Number(f.buffer_days) || 0,
        on_hand: f.on_hand === "" || f.on_hand === undefined || f.on_hand === null ? undefined : Math.max(0, Math.min(500, Number(f.on_hand))),
        website_visible: f.website_visible !== false,
        available_units_override: f.available_units_override === "" || f.available_units_override === undefined || f.available_units_override === null ? null : Math.max(0, Math.min(500, Number(f.available_units_override))),
        free_accessories: (Array.isArray(f.free_accessories)
          ? f.free_accessories
          : String(f.free_accessories || "").split(",")
        ).map(s => String(s).trim()).filter(Boolean),
        addon_product_ids: (f.addon_product_ids || []).filter(Boolean),
        option_groups: (f.option_groups || []).map(g => ({
          id: (g.id || g.label || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40) || `group-${Math.random().toString(36).slice(2, 8)}`,
          label: g.label || "", required: !!g.required,
          options: (g.options || []).map(o => ({
            id: (o.id || o.name || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40) || `opt-${Math.random().toString(36).slice(2, 8)}`,
            name: o.name || "", image: o.image || "",
            price_delta_per_day: Number(o.price_delta_per_day) || 0,
            inventory: o.inventory === "" || o.inventory === null || o.inventory === undefined ? null : Number(o.inventory),
            hidden: !!o.hidden, linked_product_id: o.linked_product_id || null,
          })),
        })),
        seo_title: (f.seo_title || "").trim() || null,
        seo_description: (f.seo_description || "").trim() || null,
        seo_slug: (f.seo_slug || "").trim() || null,
      };
      if (isNew) {
        payload.id = f.id || f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
        await axios.post(`${API}/admin/products`, payload);
      } else {
        await axios.patch(`${API}/admin/products/${f.id}`, payload);
      }
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const isStroller = f.category === "strollers";
  const isEcv = f.category?.toLowerCase().includes("ecv") || f.category?.toLowerCase().includes("wheelchair");

  const tierPreview4 = (Number(f.tier_1_3 || 0) * (1 - Number(f.tier_4_7_discount_pct ?? 15) / 100)).toFixed(2);
  const tierPreview8 = (Number(f.tier_1_3 || 0) * (1 - Number(f.tier_8_plus_discount_pct ?? f.tier_8_plus_premium_pct ?? 25) / 100)).toFixed(2);

  return (
    <div className="h-full flex flex-col" data-testid="product-editor">
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-[#FFD166]/15">
        <div className="flex items-center gap-2 min-w-0">
          {/* Back button — mobile only */}
          {onClose && (
            <button
              onClick={onClose}
              className="md:hidden flex items-center gap-1 text-[#B9C8D5] hover:text-[#FFD166] text-sm shrink-0 mr-1"
              data-testid="product-editor-back-mobile"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              <span>Back</span>
            </button>
          )}
          <h2 className="font-serif text-xl text-[#FFD166] truncate" data-testid="product-editor-title">
            {isNew ? "New Product" : f.name || "Untitled"}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <button onClick={onClose} className="hidden md:block text-[#B9C8D5] hover:text-[#F6F1E4] p-1 rounded" data-testid="product-editor-close">
              <XIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Toggle strip */}
      <div className="shrink-0 flex items-center gap-3 px-5 py-2.5 border-b border-[#FFD166]/10 bg-black/20 flex-wrap">
        {[
          { key: "active", label: "Active", help: "Available for booking" },
          { key: "website_visible", label: "Website Visible", help: "Shown on public catalog" },
          { key: "featured", label: "Featured", help: "Highlighted on homepage" },
        ].map(({ key, label, help }) => (
          <label key={key} className="flex items-center gap-2 cursor-pointer" title={help} data-testid={`toggle-${key}`}>
            <input
              type="checkbox"
              checked={!!f[key]}
              onChange={e => setF({ ...f, [key]: e.target.checked })}
              className="accent-[#FFD166]"
            />
            <span className="text-xs text-[#F6F1E4]">{label}</span>
          </label>
        ))}
      </div>

      {/* Scrollable form body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">

        {/* ── Identity */}
        <Section title="Item Info" icon={Tag} testid="section-identity">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs text-[#B9C8D5] block mb-1">Item Name</label>
              <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="e.g. Nuna MIXX Next Stroller" data-testid="pf-name" />
            </div>
            <div>
              <label className="text-xs text-[#B9C8D5] block mb-1">Tagline (short)</label>
              <Input value={f.tagline} onChange={e => setF({ ...f, tagline: e.target.value })} placeholder="One-line benefit" data-testid="pf-tagline" />
            </div>
            <div>
              <label className="text-xs text-[#B9C8D5] block mb-1">Category</label>
              <select value={f.category} onChange={e => setF({ ...f, category: e.target.value, subcategory: "" })} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm" data-testid="pf-category">
                <option value="">Choose…</option>
                {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {subcats.length > 0 && (
              <div>
                <label className="text-xs text-[#B9C8D5] block mb-1">Subcategory</label>
                <select value={f.subcategory} onChange={e => setF({ ...f, subcategory: e.target.value })} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm" data-testid="pf-subcategory">
                  <option value="">—</option>
                  {subcats.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs text-[#B9C8D5] block mb-1">Age range</label>
              <Input value={f.min_age} onChange={e => setF({ ...f, min_age: e.target.value })} placeholder="e.g. 6 mo – 50 lbs" data-testid="pf-age" />
            </div>
          </div>
        </Section>

        {/* ── Image */}
        <Section title="Item Photo" icon={Camera} testid="section-image">
          <div className="flex gap-4 items-start flex-wrap">
            {f.image && (
              <img
                src={f.image.startsWith("/api/") ? `${BACKEND_URL}${f.image}` : f.image}
                alt={f.name}
                className="w-28 h-28 object-cover rounded-xl border border-[#FFD166]/20"
              />
            )}
            <div className="flex-1 min-w-0 space-y-2">
              <label className="text-xs text-[#B9C8D5] block">Image URL</label>
              <Input value={f.image} onChange={e => setF({ ...f, image: e.target.value })} placeholder="https://… or /api/static/…" data-testid="pf-image" />
              <label className="inline-flex items-center gap-2 text-xs text-[#FFD166] cursor-pointer hover:underline" data-testid="pf-image-upload-label">
                <input type="file" accept="image/*" className="hidden" data-testid="pf-image-upload" onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  const fd = new FormData(); fd.append("file", file);
                  try {
                    const { data } = await axios.post(`${API}/admin/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setF(prev => ({ ...prev, image: data.url })); toast.success("Photo uploaded");
                  } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
                }} />
                <Camera className="w-3.5 h-3.5" /> Upload photo from device
              </label>
            </div>
          </div>
        </Section>

        {/* ── Description */}
        <Section title="Description & Features" icon={PackageIcon} testid="section-desc">
          <label className="text-xs text-[#B9C8D5] block mb-1">Description (shown to customers)</label>
          <textarea
            rows={4}
            value={f.description}
            onChange={e => setF({ ...f, description: e.target.value })}
            className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm resize-y"
            placeholder="Include what's included, model info, condition, features…"
            data-testid="pf-description"
          />
          <label className="text-xs text-[#B9C8D5] block mb-1 mt-3">Feature bullets (one per line)</label>
          <textarea
            rows={4}
            value={f.features}
            onChange={e => setF({ ...f, features: e.target.value })}
            className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm resize-y"
            placeholder={"UPF 50+ canopy\nFolds flat to 11\""}
            data-testid="pf-features"
          />
          <label className="text-xs text-[#B9C8D5] block mb-1 mt-3">Free accessories included (comma-separated)</label>
          <Input
            value={Array.isArray(f.free_accessories) ? f.free_accessories.join(", ") : f.free_accessories}
            onChange={e => setF({ ...f, free_accessories: e.target.value })}
            placeholder="Rain cover, Parent console"
            data-testid="pf-free-accessories"
          />
        </Section>

        {/* ── Pricing */}
        <Section title="Pricing" icon={Tag} testid="section-pricing">
          <div className="rounded-lg border border-[#FFD166]/15 bg-black/30 p-3 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#B9C8D5] block mb-1">1–3 nights<br />(per day)</label>
                <Input type="number" step="0.01" value={f.tier_1_3} onChange={e => setF({ ...f, tier_1_3: e.target.value })} data-testid="pf-tier1" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#B9C8D5] block mb-1">4–7 nights<br />(per day)</label>
                <Input
                  type="number" step="0.01"
                  value={f.tier_auto_calc ? tierPreview4 : f.tier_4_7}
                  disabled={!!f.tier_auto_calc}
                  onChange={e => setF({ ...f, tier_4_7: e.target.value })}
                  data-testid="pf-tier2"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#B9C8D5] block mb-1">8+ nights<br />(per day)</label>
                <Input
                  type="number" step="0.01"
                  value={f.tier_auto_calc ? tierPreview8 : f.tier_8_plus}
                  disabled={!!f.tier_auto_calc}
                  onChange={e => setF({ ...f, tier_8_plus: e.target.value })}
                  data-testid="pf-tier3"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!f.tier_auto_calc} onChange={e => setF({ ...f, tier_auto_calc: e.target.checked })} className="accent-[#FFD166]" data-testid="pf-tier-autocalc" />
              <span className="text-xs text-[#F6F1E4]">Auto-calculate 4–7 and 8+ tiers from base rate</span>
            </label>
            {f.tier_auto_calc && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#B9C8D5] block mb-1">4–7 discount (%)</label>
                  <Input type="number" min="0" max="100" value={f.tier_4_7_discount_pct ?? 15} onChange={e => setF({ ...f, tier_4_7_discount_pct: e.target.value })} data-testid="pf-tier2-pct" />
                </div>
                <div>
                  <label className="text-xs text-[#B9C8D5] block mb-1">8+ discount (%)</label>
                  <Input type="number" min="0" max="100" value={f.tier_8_plus_discount_pct ?? f.tier_8_plus_premium_pct ?? 25} onChange={e => setF({ ...f, tier_8_plus_discount_pct: e.target.value })} data-testid="pf-tier3-pct" />
                </div>
              </div>
            )}
          </div>
        </Section>

        {/* ── Inventory — the KEY section */}
        <Section title="Inventory" icon={PackageIcon} testid="section-inventory">
          <div className="space-y-4">

            {/* Stock count control */}
            <div>
              <label className="text-xs text-[#B9C8D5] block mb-2">
                Total units in fleet
                {onHandLive !== null && <span className="text-[#FFD166] ml-1">(live: {onHandLive})</span>}
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => adjustStock(-1)}
                  disabled={adjustingStock || isNew}
                  className="w-9 h-9 rounded-lg border border-[#FFD166]/30 text-[#FFD166] flex items-center justify-center hover:bg-[#FFD166]/10 disabled:opacity-40 transition-colors"
                  data-testid="stock-minus"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <Input
                  type="number"
                  min="0"
                  max="500"
                  value={f.on_hand ?? ""}
                  onChange={e => setStockDirectly(e.target.value)}
                  onBlur={isNew ? undefined : applyStockInput}
                  placeholder={isNew ? "e.g. 5" : "current count"}
                  className="w-24 text-center"
                  data-testid="pf-on-hand"
                />
                <button
                  type="button"
                  onClick={() => adjustStock(+1)}
                  disabled={adjustingStock || isNew}
                  className="w-9 h-9 rounded-lg border border-[#FFD166]/30 text-[#FFD166] flex items-center justify-center hover:bg-[#FFD166]/10 disabled:opacity-40 transition-colors"
                  data-testid="stock-plus"
                >
                  <Plus className="w-4 h-4" />
                </button>
                {adjustingStock && <RefreshCw className="w-4 h-4 text-[#FFD166] animate-spin" />}
              </div>
              <p className="text-[10px] text-[#B9C8D5] mt-1">
                Raising this adds new barcoded units. Lowering it retires the newest unassigned units. Change takes effect immediately for checkout availability.
              </p>
            </div>

            {/* Available cap */}
            <div className="rounded-lg border border-[#FFD166]/15 bg-black/20 p-3">
              <label className="text-xs text-[#B9C8D5] block mb-1">Max bookable at once (optional)</label>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#F6F1E4]">
                  <input
                    type="radio"
                    name={`inv-cap-${f.id || "new"}`}
                    checked={f.available_units_override === "" || f.available_units_override === null || f.available_units_override === undefined}
                    onChange={() => setF({ ...f, available_units_override: "" })}
                    className="accent-[#FFD166]"
                    data-testid="pf-no-cap"
                  />
                  No limit (use fleet count)
                </label>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#F6F1E4]">
                  <input
                    type="radio"
                    name={`inv-cap-${f.id || "new"}`}
                    checked={f.available_units_override !== "" && f.available_units_override !== null && f.available_units_override !== undefined}
                    onChange={() => setF({ ...f, available_units_override: onHandLive ?? 1 })}
                    className="accent-[#FFD166]"
                    data-testid="pf-with-cap"
                  />
                  Limit to
                </label>
                <Input
                  type="number" min="0" max="500"
                  value={f.available_units_override ?? ""}
                  onChange={e => setF({ ...f, available_units_override: e.target.value })}
                  disabled={f.available_units_override === "" || f.available_units_override === null || f.available_units_override === undefined}
                  className="w-20"
                  placeholder="N"
                  data-testid="pf-avail-override"
                />
                <span className="text-xs text-[#B9C8D5]">units</span>
              </div>
              <p className="text-[10px] text-[#B9C8D5] mt-1.5">
                Caps simultaneous bookings without retiring physical units. Useful when holding units back for corporate/admin use.
              </p>
            </div>

            <div>
              <label className="text-xs text-[#B9C8D5] block mb-1">Buffer days between rentals</label>
              <Input type="number" min="0" value={f.buffer_days} onChange={e => setF({ ...f, buffer_days: e.target.value })} className="w-24" data-testid="pf-buffer" />
              <p className="text-[10px] text-[#B9C8D5] mt-1">Days reserved for cleaning between back-to-back rentals.</p>
            </div>

            {/* Physical unit list */}
            {!isNew && (
              <div>
                <div className="text-xs text-[#B9C8D5] uppercase tracking-wider mb-2">Physical Units</div>
                <InlineInventory
                  productId={f.id}
                  onHandLive={onHandLive}
                  onRefresh={loadOnHand}
                />
              </div>
            )}
          </div>
        </Section>

        {/* ── Type-specific attributes */}
        {(isStroller || isEcv) && (
          <Section title={isStroller ? "Stroller Attributes" : "ECV / Wheelchair Attributes"} icon={PackageIcon} testid="section-attributes">
            {isStroller && <StrollerAttributesEditor value={f} onChange={setF} />}
            {isEcv && <EcvAttributesEditor value={f} onChange={setF} />}
          </Section>
        )}

        {/* ── Bookings */}
        {!isNew && (
          <Section title="Bookings" icon={Calendar} defaultOpen={false} testid="section-bookings">
            <BookingsSection productId={f.id} />
          </Section>
        )}

        {/* ── Cross-sells & add-ons */}
        <Section title="Related & Add-ons" icon={ShoppingBag} defaultOpen={false} testid="section-related">
          <Field label="Cross-sells (shown on product page)">
            <select
              multiple
              value={f.related}
              onChange={e => setF({ ...f, related: Array.from(e.target.selectedOptions).map(o => o.value) })}
              className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg h-24 text-sm"
              data-testid="pf-related"
            >
              {allProducts.filter(p => p.id !== f.id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
        </Section>

        {/* ── SEO (collapsed by default) */}
        <Section title="SEO" icon={Layers} defaultOpen={false} testid="section-seo">
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#B9C8D5] block mb-1">
                Page title <span className={`ml-1 ${(f.seo_title || "").length === 0 ? "text-[#B9C8D5]" : (f.seo_title || "").length <= 60 ? "text-emerald-400" : "text-red-400"}`}>({(f.seo_title || "").length}/60)</span>
              </label>
              <Input value={f.seo_title} onChange={e => setF({ ...f, seo_title: e.target.value })} placeholder="Defaults to product name" data-testid="pf-seo-title" />
            </div>
            <div>
              <label className="text-xs text-[#B9C8D5] block mb-1">
                Meta description <span className={`ml-1 ${(f.seo_description || "").length <= 160 ? "text-emerald-400" : "text-[#F4A93A]"}`}>({(f.seo_description || "").length}/160)</span>
              </label>
              <textarea
                rows={2}
                value={f.seo_description}
                onChange={e => setF({ ...f, seo_description: e.target.value })}
                className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm resize-none"
                placeholder="Defaults to tagline"
                data-testid="pf-seo-desc"
              />
            </div>
            <div>
              <label className="text-xs text-[#B9C8D5] block mb-1">URL slug</label>
              <Input
                value={f.seo_slug}
                onChange={e => { setSlugManuallyEdited(true); setF({ ...f, seo_slug: e.target.value }); }}
                placeholder="auto-generated-from-name"
                data-testid="pf-seo-slug"
              />
            </div>
          </div>
        </Section>

      </div>

      {/* Footer actions */}
      <div className="shrink-0 flex items-center justify-between gap-3 px-5 py-3 border-t border-[#FFD166]/15 bg-black/30">
        {onClose && (
          <button type="button" onClick={onClose} className="text-xs text-[#B9C8D5] hover:text-[#F6F1E4] px-3 py-2 rounded-lg border border-[#B9C8D5]/25" data-testid="product-editor-cancel">
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FFD166] text-[#040A0F] font-medium text-sm hover:bg-[#FFC933] disabled:opacity-50 transition-colors ml-auto"
          data-testid="product-editor-save"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving…" : "Save Product"}
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────── MAIN PAGE ── */
export const Products = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [selected, setSelected] = useState(null); // product id
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState(""); // "" = all, else category id

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([
      axios.get(`${API}/admin/products`),
      axios.get(`${API}/admin/categories`),
    ]);
    setItems(p.data.products);
    setCats(c.data.categories);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => {
    let list = items;
    if (filter) list = list.filter(p => p.category === filter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q));
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [items, filter, query]);

  const activeDraft = useMemo(() => {
    if (creating) return { ...emptyProduct };
    if (selected) return items.find(p => p.id === selected) || null;
    return null;
  }, [selected, creating, items]);

  const handleSaved = () => {
    load();
    setCreating(false);
    // keep selection so admin can see the updated product
  };

  const handleNew = () => { setCreating(true); setSelected(null); };
  const handleClose = () => { setCreating(false); setSelected(null); };

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden" data-testid="admin-products-page">

      {/* LEFT PANEL — product list (hidden on mobile when editor is open) */}
      <div className={`flex-col border-r border-[#FFD166]/15 bg-black/20 transition-all ${activeDraft ? "hidden md:flex md:w-64 lg:w-72 shrink-0" : "flex w-full"}`}>
        {/* List header */}
        <div className="p-3 border-b border-[#FFD166]/15 space-y-2">
          <div className="flex items-center justify-between">
            <h1 className="font-serif text-xl text-[#F6F1E4]">Products</h1>
            <button
              onClick={handleNew}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FFD166] text-[#040A0F] text-xs font-medium hover:bg-[#FFC933] transition-colors"
              data-testid="product-new-btn"
            >
              <Plus className="w-3.5 h-3.5" /> New
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#B9C8D5]" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search products…"
              className="w-full bg-black/30 border border-[#FFD166]/20 text-[#F6F1E4] text-sm pl-8 pr-3 py-1.5 rounded-lg placeholder-[#B9C8D5]/50"
              data-testid="product-search"
            />
          </div>
          {/* Category filter chips */}
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => setFilter("")}
              className={`text-[10px] px-2 py-0.5 rounded-full border ${!filter ? "bg-[#FFD166]/15 border-[#FFD166]/50 text-[#FFD166]" : "border-[#B9C8D5]/20 text-[#B9C8D5] hover:border-[#FFD166]/30"}`}
              data-testid="filter-all"
            >All</button>
            {cats.map(c => (
              <button
                key={c.id}
                onClick={() => setFilter(filter === c.id ? "" : c.id)}
                className={`text-[10px] px-2 py-0.5 rounded-full border ${filter === c.id ? "bg-[#FFD166]/15 border-[#FFD166]/50 text-[#FFD166]" : "border-[#B9C8D5]/20 text-[#B9C8D5] hover:border-[#FFD166]/30"}`}
                data-testid={`filter-cat-${c.id}`}
              >{c.name}</button>
            ))}
          </div>
        </div>

        {/* List body */}
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {filteredItems.length === 0 ? (
            <p className="text-xs text-[#B9C8D5] text-center py-8">No products found.</p>
          ) : (
            filteredItems.map(p => (
              <ProductListItem
                key={p.id}
                p={p}
                selected={selected === p.id && !creating}
                onClick={() => { setSelected(p.id); setCreating(false); }}
              />
            ))
          )}
        </div>

        {/* Stats footer */}
        <div className="px-3 py-2 border-t border-[#FFD166]/10 text-[10px] text-[#B9C8D5]">
          {filteredItems.length} of {items.length} products
        </div>
      </div>

      {/* RIGHT PANEL — edit form */}
      {activeDraft ? (
        <div className="flex-1 min-w-0 overflow-hidden">
          <ProductEditor
            key={activeDraft.id || "new"}
            draft={activeDraft}
            cats={cats}
            allProducts={items}
            onClose={handleClose}
            onSaved={handleSaved}
          />
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-[#B9C8D5]" data-testid="product-editor-empty">
          <div className="text-center space-y-2">
            <PackageIcon className="w-10 h-10 mx-auto opacity-30" />
            <p className="text-sm">Select a product to edit, or create a new one.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
